import React, { memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Button,
  ButtonGroup,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from "@chakra-ui/react";
import { FaChevronDown } from "react-icons/fa";
import * as PostEditorActions from "./PostEditorActions.constants";
import PostEditorDateTimeButton from "./PostEditorDateTimeButton";
import PostEditorDateTimePicker from "./PostEditorDateTimePicker";
import { translate as trans } from "../Trans/Trans";

const PostEditorEditDraftPostButtons = ({
  isLoading,
  isDisabled,
  canEditPosts,
  date,
  time,
  timezone,
  defaultTimezone,
  onTimeChange,
  onSave,
  onAddSchedule,
  onAddBuffer,
  onPublishNow,
}) => {
  const convertToPostActions = useMemo(
    () => [
      {
        key: PostEditorActions.ADD_SCHEDULE,
        onClick: onAddSchedule,
      },
      {
        key: PostEditorActions.ADD_BUFFER,
        onClick: onAddBuffer,
      },
      {
        key: PostEditorActions.PUBLISH_NOW,
        onClick: onPublishNow,
      },
    ],
    [onAddSchedule, onAddBuffer, onPublishNow]
  );

  const handleActionClick = useCallback(
    (item) => () => {
      item.onClick?.();
    },
    []
  );

  return (
    <ButtonGroup size="sm">
      <Popover isLazy placement="bottom-start">
        <PopoverTrigger>
          <PostEditorDateTimeButton
            variant="outline"
            date={date}
            time={time}
            timezone={timezone}
            isDisabled={!!isLoading}
          />
        </PopoverTrigger>
        <PopoverContent width="auto" padding="3">
          <PopoverArrow />
          <PostEditorDateTimePicker
            showTimeClearButton
            date={date}
            time={time}
            timezone={timezone}
            defaultTimezone={defaultTimezone}
            onChange={onTimeChange}
          />
        </PopoverContent>
      </Popover>
      <ButtonGroup size="sm" isAttached variant="solid">
        <Button
          variant="solid"
          onClick={onSave}
          isDisabled={isDisabled || !!isLoading}
          isLoading={isLoading === PostEditorActions.SAVE_DRAFT}
        >
          {trans("post_editor.actions.save_draft")}
        </Button>
        {canEditPosts && (
          <Menu isLazy placement="bottom-end">
            <MenuButton
              as={IconButton}
              icon={<Icon as={FaChevronDown} fontSize="xs" />}
              borderLeft="1px solid"
              borderLeftColor="whiteAlpha.400"
              isDisabled={isDisabled || !!isLoading}
            />
            <MenuList fontSize="sm" sx={{ background: "gray.600" }}>
              {convertToPostActions.map((item) => (
                <MenuItem key={item.key} onClick={handleActionClick(item)}>
                  {trans(`post_editor.actions.${item.key}`)}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        )}
      </ButtonGroup>
    </ButtonGroup>
  );
};

PostEditorEditDraftPostButtons.propTypes = {
  isLoading: PropTypes.oneOf(Object.values(PostEditorActions)),
  isDisabled: PropTypes.bool,
  date: PropTypes.string,
  time: PropTypes.string,
  timezone: PropTypes.string,
  defaultTimezone: PropTypes.string,
  onTimeChange: PropTypes.func,
  onSave: PropTypes.func,
};

PostEditorEditDraftPostButtons.defaultProps = {
  isLoading: undefined,
  isDisabled: undefined,
  date: undefined,
  time: undefined,
  timezone: undefined,
  defaultTimezone: undefined,
  onTimeChange: undefined,
  onSave: undefined,
};

export default memo(PostEditorEditDraftPostButtons);
